import {
  createTypesenseClient,
  getCollectionNames,
  getTypesenseSearchConfigError,
  TYPESENSE_SEARCH_CONFIG_ERROR,
} from '../typesense';

export const PORTFOLIO_TYPESENSE_QUERY_BY = 'name,description';

export interface PortfolioSearchFetchResult {
  beerIds: string[];
  error: string | null;
}

export interface PortfolioTypesenseSearchParams {
  q: string;
  query_by: string;
  per_page: number;
  page: number;
  filter_by?: string;
}

/**
 * Builds a Typesense filter_by clause scoped to portfolio brewery ids.
 * Uses availableBreweryIds array-contains-any semantics via OR of individual contains.
 */
export function buildPortfolioTypesenseFilter(
  breweryIds: string[],
): string | undefined {
  if (!Array.isArray(breweryIds) || breweryIds.length === 0) {
    return undefined;
  }

  const normalizedIds = breweryIds
    .map((breweryId) =>
      typeof breweryId === 'string' ? breweryId.trim() : '',
    )
    .filter((breweryId) => breweryId.length > 0);

  if (normalizedIds.length === 0) {
    return undefined;
  }

  if (normalizedIds.length === 1) {
    return `availableBreweryIds:=[${normalizedIds[0]}]`;
  }

  const clauses = normalizedIds.map(
    (breweryId) => `availableBreweryIds:=[${breweryId}]`,
  );
  return clauses.join(' || ');
}

/**
 * Builds Typesense search parameters for brewhouse portfolio text search.
 */
export function buildPortfolioTypesenseSearchParams(
  searchQuery: string,
  breweryIds: string[],
  options: { page?: number; perPage?: number } = {},
): PortfolioTypesenseSearchParams {
  const { page = 1, perPage = 100 } = options;
  const trimmed =
    typeof searchQuery === 'string' ? searchQuery.trim() : '';

  const params: PortfolioTypesenseSearchParams = {
    q: trimmed.length > 0 ? trimmed : '*',
    query_by: PORTFOLIO_TYPESENSE_QUERY_BY,
    per_page: Math.min(100, Math.max(1, perPage)),
    page: Math.max(1, page),
  };

  const filterBy = buildPortfolioTypesenseFilter(breweryIds);
  if (filterBy) {
    params.filter_by = filterBy;
  }

  return params;
}

/**
 * Extracts beer document ids from Typesense search hits.
 */
export function extractBeerIdsFromTypesenseHits(
  hits: Array<{ document?: { id?: string } }> | undefined,
): string[] {
  if (!Array.isArray(hits)) {
    return [];
  }

  const ids: string[] = [];
  hits.forEach((hit) => {
    const docId =
      hit?.document?.id != null ? String(hit.document.id) : '';
    if (docId.length > 0) {
      ids.push(docId);
    }
  });

  return ids;
}

/**
 * Intersects catalog beers with Typesense result ids, preserving catalog order.
 */
export function intersectBeersWithSearchIds<T extends { id?: string }>(
  beers: T[],
  searchIds: string[],
): T[] {
  if (!Array.isArray(beers) || beers.length === 0) {
    return [];
  }
  if (!Array.isArray(searchIds) || searchIds.length === 0) {
    return [];
  }

  const idSet = new Set(searchIds);
  return beers.filter((beer) => {
    const beerId = typeof beer.id === 'string' ? beer.id : '';
    return beerId.length > 0 && idSet.has(beerId);
  });
}

/**
 * Returns true when the search query is active (non-empty after trim).
 */
export function isPortfolioSearchActive(searchQuery: string): boolean {
  return typeof searchQuery === 'string' && searchQuery.trim().length > 0;
}

function resolveTypesenseBeerCollectionName(): string | null {
  const mode = (import.meta.env.MODE || 'development').toLowerCase();
  const env = ['development', 'staging', 'production'].includes(mode)
    ? mode
    : 'development';
  const { beer: collectionName } = getCollectionNames(env);
  return collectionName || null;
}

/**
 * Executes a portfolio-scoped Typesense search and returns matching beer ids.
 */
export async function fetchPortfolioSearchBeerIds(
  searchQuery: string,
  portfolioBreweryIds: string[],
): Promise<PortfolioSearchFetchResult> {
  const configError = getTypesenseSearchConfigError();
  if (configError) {
    return { beerIds: [], error: configError };
  }

  const collectionName = resolveTypesenseBeerCollectionName();
  if (!collectionName) {
    return { beerIds: [], error: TYPESENSE_SEARCH_CONFIG_ERROR };
  }

  const client = createTypesenseClient();
  if (!client) {
    return { beerIds: [], error: TYPESENSE_SEARCH_CONFIG_ERROR };
  }

  const searchParams = buildPortfolioTypesenseSearchParams(
    searchQuery,
    portfolioBreweryIds,
  );

  try {
    const searchResponse = await client
      .collections(collectionName)
      .documents()
      .search(searchParams);

    const hits = searchResponse?.hits ?? [];
    return {
      beerIds: extractBeerIdsFromTypesenseHits(hits),
      error: null,
    };
  } catch (searchError: unknown) {
    console.error('Portfolio Typesense search failed:', searchError);
    const message =
      searchError instanceof Error
        ? searchError.message
        : 'Failed to search beers. Please try again.';
    return { beerIds: [], error: message };
  }
}
